// cart-product-swap-advanced.js
// Script avançado para substituir itens do carrinho por produtos genéricos ao clicar em "Finalizar Compra"
// Suporta mapeamentos específicos de produtos e regras por categoria
// Versão corrigida para evitar erro 422 e permitir checkout normal quando não há mapeamentos

(function() {
  "use strict";

  // Função para exibir mensagens de log com prefixo
  function logMessage(message, data) {
    console.log("Cart Product Swap (Advanced):", message, data !== undefined ? data : "");
  }

  // Função para mostrar um indicador de carregamento simples (TEXTO APENAS)
  function showLoadingIndicator(button) {
    if (button) {
      button.disabled = true;
      button.textContent = "Processando..."; // Alterado para texto simples
    }
  }

  // Função para restaurar o botão do indicador de carregamento
  function hideLoadingIndicator(button, originalText) {
    if (button) {
      button.disabled = false;
      button.innerHTML = originalText; // Mantém innerHTML para restaurar o texto/ícone original se houver
    }
  }

  // Função para obter as configurações do tema
  function getThemeSettings() {
    return {
      enableProductSwap: window.enableProductSwap !== undefined ? window.enableProductSwap : true,
      genericProductHandleDefault: window.genericProductHandleCheckout || "",
      
      // Mapeamentos de produtos específicos
      productMappings: [
        { original: window.productMappingOriginal1 || "", generic: window.productMappingGeneric1 || "" },
        { original: window.productMappingOriginal2 || "", generic: window.productMappingGeneric2 || "" },
        { original: window.productMappingOriginal3 || "", generic: window.productMappingGeneric3 || "" },
        { original: window.productMappingOriginal4 || "", generic: window.productMappingGeneric4 || "" },
        { original: window.productMappingOriginal5 || "", generic: window.productMappingGeneric5 || "" },
        { original: window.productMappingOriginal6 || "", generic: window.productMappingGeneric6 || "" },
        { original: window.productMappingOriginal7 || "", generic: window.productMappingGeneric7 || "" },
        { original: window.productMappingOriginal8 || "", generic: window.productMappingGeneric8 || "" },
        { original: window.productMappingOriginal9 || "", generic: window.productMappingGeneric9 || "" },
        { original: window.productMappingOriginal10 || "", generic: window.productMappingGeneric10 || "" }
      ].filter(mapping => mapping.original && mapping.generic),
      
      // Mapeamentos de categorias
      categoryMappings: [
        { collection: window.categoryMappingCollection1 || "", generic: window.categoryMappingGeneric1 || "" },
        { collection: window.categoryMappingCollection2 || "", generic: window.categoryMappingGeneric2 || "" },
        { collection: window.categoryMappingCollection3 || "", generic: window.categoryMappingGeneric3 || "" }
      ].filter(mapping => mapping.collection && mapping.generic)
    };
  }

  // Função para determinar qual produto genérico usar para um item específico
  async function determineGenericProduct(item) {
    const settings = getThemeSettings();
    
    // Verificar se existe um mapeamento específico para este produto
    const productHandle = item.handle;
    const specificMapping = settings.productMappings.find(mapping => mapping.original === productHandle);
    
    if (specificMapping) {
      logMessage(`Mapeamento específico encontrado para ${productHandle}: ${specificMapping.generic}`);
      return specificMapping.generic;
    }
    
    // Verificar se o produto pertence a alguma categoria mapeada
    if (settings.categoryMappings.length > 0) {
      try {
        const productResponse = await fetch(`/products/${productHandle}.json`);
        if (productResponse.ok) {
          const productData = await productResponse.json();
          
          // Obter as coleções do produto (isso requer uma implementação adicional)
          // Como o Shopify não fornece diretamente as coleções via API, podemos usar metafields ou tags
          // Para este exemplo, vamos supor que as tags do produto incluem os handles das coleções
          if (productData.product && productData.product.tags) {
            const productTags = productData.product.tags;
            
            // Verificar se alguma tag corresponde a uma coleção mapeada
            for (const categoryMapping of settings.categoryMappings) {
              if (productTags.includes(categoryMapping.collection)) {
                logMessage(`Mapeamento de categoria encontrado para ${productHandle} via tag ${categoryMapping.collection}: ${categoryMapping.generic}`);
                return categoryMapping.generic;
              }
            }
          }
        }
      } catch (error) {
        logMessage("Erro ao obter informações do produto para verificação de categoria:", error);
      }
    }
    
    // Se não houver mapeamento específico ou por categoria, verificar se há um padrão
    if (settings.genericProductHandleDefault) {
      return settings.genericProductHandleDefault;
    }
    
    // Se não houver nenhum mapeamento, retornar null para manter o produto original
    return null;
  }

  // Função para obter a variante disponível de um produto
  async function getAvailableVariant(productHandle) {
    try {
      const productResponse = await fetch("/products/" + productHandle + ".js");
      if (!productResponse.ok) throw new Error("Produto não encontrado: " + productResponse.status);
      
      const productData = await productResponse.json();
      
      if (!productData.variants || productData.variants.length === 0) {
        throw new Error("Produto não possui variantes disponíveis.");
      }
      
      // Pega a primeira variante disponível
      const firstAvailableVariant = productData.variants.find(variant => variant.available);
      if (!firstAvailableVariant) {
        throw new Error("Produto não possui variantes COM ESTOQUE.");
      }
      
      return {
        id: firstAvailableVariant.id,
        productTitle: productData.title,
        variantTitle: firstAvailableVariant.title
      };
    } catch (error) {
      throw new Error(`Erro ao obter variante para ${productHandle}: ${error.message}`);
    }
  }

  // Função para remover um item específico do carrinho
  async function removeItemFromCart(itemKey) {
    try {
      const response = await fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          id: itemKey,
          quantity: 0
        })
      });
      
      if (!response.ok) {
        throw new Error(`Falha ao remover item ${itemKey}: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      logMessage(`Erro ao remover item ${itemKey}:`, error);
      throw error;
    }
  }

  // Função principal para inicializar a lógica de troca
  async function initCartProductSwap() {
    logMessage("Init Advanced v3..."); // Versão avançada corrigida do script
    
    const settings = getThemeSettings();
    
    // Verificar se a funcionalidade está ativada
    if (!settings.enableProductSwap) {
      logMessage("Funcionalidade de substituição de produtos desativada nas configurações.");
      return;
    }
    
    logMessage("Configurações carregadas:", settings);

    // Tenta o seletor mais específico primeiro, depois um mais genérico
    let checkoutButton = document.querySelector("form.cart-recap button[name=\"checkout\"]");
    if (!checkoutButton) {
        checkoutButton = document.querySelector("button[name=\"checkout\"]");
        if(checkoutButton) {
            logMessage("Botão de checkout encontrado com seletor alternativo (button[name='checkout'])");
        } else {
            logMessage("Botão de checkout não encontrado na página do carrinho com seletores primário e alternativo.");
            return;
        }
    } else {
        logMessage("Botão de checkout encontrado com seletor primário (form.cart-recap button[name='checkout'])");
    }
    
    const originalButtonText = checkoutButton.innerHTML;

    checkoutButton.addEventListener("click", async function(event) {
      event.preventDefault(); 
      logMessage("Botão de checkout clicado.");
      showLoadingIndicator(checkoutButton);

      try {
        // Obter itens do carrinho
        logMessage("Obtendo itens do carrinho...");
        const cartResponse = await fetch("/cart.js");
        if (!cartResponse.ok) throw new Error("Falha ao obter carrinho: " + cartResponse.status);
        const cartData = await cartResponse.json();
        logMessage("Carrinho atual:", cartData);

        if (!cartData.items || cartData.items.length === 0) {
          logMessage("Carrinho vazio. Redirecionando para o checkout...");
          window.location.href = "/checkout";
          return;
        }
        
        // Verificar se há produtos para substituir
        let hasProductsToReplace = false;
        const genericProductGroups = {};
        
        // Processar cada item e determinar seu produto genérico correspondente
        for (const item of cartData.items) {
          try {
            const genericHandle = await determineGenericProduct(item);
            
            if (!genericHandle) {
              logMessage(`Nenhum produto genérico definido para ${item.handle || item.title}, mantendo produto original.`);
              continue;
            }
            
            // Marcar que há produtos para substituir
            hasProductsToReplace = true;
            
            // Agrupar por produto genérico
            if (!genericProductGroups[genericHandle]) {
              genericProductGroups[genericHandle] = 0;
            }
            
            // Adicionar quantidade deste item ao grupo
            genericProductGroups[genericHandle] += item.quantity;
            
          } catch (error) {
            logMessage(`Erro ao processar item ${item.title}:`, error);
          }
        }
        
        // CORREÇÃO: Se não houver produtos para substituir, continuar para o checkout normalmente
        if (!hasProductsToReplace) {
          logMessage("Nenhum produto no carrinho tem mapeamento para substituição. Continuando para o checkout normalmente...");
          window.location.href = "/checkout";
          return;
        }
        
        logMessage("Grupos de produtos genéricos:", genericProductGroups);
        
        // CORREÇÃO: Remover itens um por um para evitar erro 422
        logMessage("Removendo itens do carrinho um por um...");
        for (const item of cartData.items) {
          try {
            await removeItemFromCart(item.key);
            logMessage(`Item ${item.title} removido com sucesso`);
          } catch (error) {
            logMessage(`Erro ao remover item ${item.title}:`, error);
            // Continuar mesmo se houver erro em um item específico
          }
        }
        
        // Verificar se o carrinho está vazio após as remoções
        const checkCartResponse = await fetch("/cart.js");
        const checkCartData = await checkCartResponse.json();
        
        if (checkCartData.items && checkCartData.items.length > 0) {
          logMessage("Aviso: Nem todos os itens foram removidos do carrinho.");
        } else {
          logMessage("Carrinho limpo com sucesso");
        }
        
        // Adicionar cada produto genérico com sua quantidade total
        for (const [genericHandle, quantity] of Object.entries(genericProductGroups)) {
          try {
            logMessage(`Processando produto genérico: ${genericHandle} (quantidade: ${quantity})`);
            
            // Obter variante disponível
            const variantInfo = await getAvailableVariant(genericHandle);
            
            logMessage(`Adicionando ${quantity} unidades de ${variantInfo.productTitle} (${variantInfo.variantTitle}) ao carrinho...`);
            
            const addResponse = await fetch("/cart/add.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json"
              },
              body: JSON.stringify({
                id: variantInfo.id,
                quantity: quantity
              })
            });
            
            if (!addResponse.ok) {
              const errorData = await addResponse.json().catch(() => null);
              throw new Error(`Falha ao adicionar produto genérico: ${addResponse.status} ${errorData ? "- " + errorData.description : ""}`);
            }
            
            logMessage(`Produto genérico ${genericHandle} adicionado com sucesso`);
            
          } catch (error) {
            logMessage(`Erro ao adicionar produto genérico ${genericHandle}:`, error);
            throw error; // Propagar erro para tratamento global
          }
        }
        
        logMessage("Todos os produtos genéricos foram adicionados. Redirecionando para o checkout...");
        window.location.href = "/checkout";

      } catch (error) {
        logMessage("Erro durante o processo de troca de produto:", error);
        alert("Ocorreu um erro ao tentar atualizar seu carrinho para o produto especial. Por favor, tente novamente ou contate o suporte.\nDetalhe: " + error.message);
        hideLoadingIndicator(checkoutButton, originalButtonText);
      }
    });

    logMessage("Event listener adicionado ao botão de checkout.");
  }

  // Garante que o DOM esteja carregado antes de executar o script
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initCartProductSwap);
  } else {
    initCartProductSwap();
  }

})();
